import type { IntakeDraft } from "~/helpers/aiChat/fernieIntake";

import { alpha, Box, CircularProgress, Icon } from "@mui/material";

import { CancelButton, SubmitButton } from "~/components/Buttons";
import FernieCardHeader from "~/components/FernieCardHeader";
import FernieCardShell from "~/components/FernieCardShell";
import fernieButtonSx from "~/components/fernie/fernieButtonSx";
import { BodyTextSmall, CaptionText } from "~/components/Typographies";
import { intakeKindLabel, intakeKindNoun, severityLabel } from "~/helpers/aiChat/fernieIntake";

interface FernieIntakeCardProps {
	busy: boolean;
	draft: IntakeDraft;
	onCancel: () => void;
	onSubmit: () => void;
}

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
	<CaptionText
		bold
		sx={{ color: "text.disabled", display: "block", letterSpacing: "0.06em", mb: 0.5, textTransform: "uppercase" }}
	>
		{children}
	</CaptionText>
);

const ContextRow = ({ icon, label, value }: { icon: string; label: string; value: string }) => (
	<Box sx={{ alignItems: "baseline", display: "flex", gap: 1, py: 0.4 }}>
		<Icon sx={{ color: "success.main", fontSize: 14, position: "relative", top: "2px" }}>{icon}</Icon>
		<CaptionText sx={{ color: "text.disabled", letterSpacing: "0.04em", minWidth: 66, textTransform: "uppercase" }}>
			{label}
		</CaptionText>
		<BodyTextSmall sx={{ color: "text.primary", flex: 1, wordBreak: "break-word" }}>{value}</BodyTextSmall>
	</Box>
);

const TypeChip = ({ draft }: { draft: IntakeDraft }) => {
	const bug = draft.intakeKind === "BUG";
	return (
		<Box
			sx={{
				alignItems: "center",
				backgroundColor: (theme) => alpha(theme.palette[bug ? "error" : "primary"].main, 0.1),
				borderRadius: "6px",
				color: bug ? "error.main" : "primary.main",
				display: "inline-flex",
				fontSize: "0.6rem",
				fontWeight: 700,
				gap: 0.4,
				letterSpacing: "0.06em",
				px: 0.9,
				py: 0.35,
				textTransform: "uppercase"
			}}
		>
			<Icon sx={{ fontSize: 13 }}>{bug ? "bug_report" : "auto_awesome"}</Icon>
			{intakeKindLabel(draft.intakeKind)}
		</Box>
	);
};

const CaptureCard = ({ busy, draft, onCancel, onSubmit }: FernieIntakeCardProps) => (
	<FernieCardShell accent="primary" branded>
		<FernieCardHeader accent="primary" branded pillLabel={`Capture · ${intakeKindLabel(draft.intakeKind)}`} />
		<Box sx={{ px: 2, pt: 1.25 }}>
			<TypeChip draft={draft} />
		</Box>

		<Box sx={{ px: 2, pt: 1.25 }}>
			<FieldLabel>Title</FieldLabel>
			<BodyTextSmall bold sx={{ color: "text.primary", wordBreak: "break-word" }}>
				{draft.title}
			</BodyTextSmall>
		</Box>

		<Box sx={{ px: 2, pt: 1.25 }}>
			<FieldLabel>Description</FieldLabel>
			<BodyTextSmall sx={{ color: "text.secondary", lineHeight: 1.45, wordBreak: "break-word" }}>
				{draft.description}
			</BodyTextSmall>
		</Box>

		{draft.severity && (
			<Box sx={{ px: 2, pt: 1.25 }}>
				<FieldLabel>Severity</FieldLabel>
				<BodyTextSmall sx={{ color: "text.secondary" }}>{severityLabel(draft.severity)}</BodyTextSmall>
			</Box>
		)}

		<Box sx={{ px: 2, pt: 1.5 }}>
			<FieldLabel>Auto-captured context</FieldLabel>
			<Box
				sx={{
					backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
					border: "1px solid",
					borderColor: (theme) => alpha(theme.palette.success.main, 0.25),
					borderRadius: "10px",
					px: 1.25,
					py: 0.75
				}}
			>
				<ContextRow icon="place" label="Page" value={draft.context.pageLabel} />
				<ContextRow icon="business" label="Tenant" value={draft.context.tenantName} />
				<ContextRow icon="person" label="User" value={draft.context.userName} />
				<ContextRow icon="commit" label="Version" value={draft.context.appVersion} />
			</Box>
		</Box>

		<Box
			sx={{
				borderColor: "divider",
				borderTop: "1px solid",
				display: "flex",
				gap: 1,
				mt: 1.5,
				px: 2,
				py: 1.25
			}}
		>
			<SubmitButton
				disabled={busy}
				fullWidth={false}
				onClick={onSubmit}
				size="small"
				startIcon={busy ? undefined : <Icon>send</Icon>}
				sx={{
					...fernieButtonSx.focusRing,
					borderRadius: "8px",
					flex: 1,
					fontSize: "0.78rem",
					minWidth: 0,
					textTransform: "none"
				}}
			>
				{busy && <CircularProgress color="inherit" size={14} />}
				<Box component="span" sx={busy ? fernieButtonSx.visuallyHidden : undefined}>
					Submit to Silver Fern
				</Box>
			</SubmitButton>
			<CancelButton
				disabled={busy}
				onClick={onCancel}
				size="small"
				sx={{
					...fernieButtonSx.focusRing,
					borderRadius: "8px",
					fontSize: "0.78rem",
					textTransform: "none"
				}}
			/>
		</Box>
	</FernieCardShell>
);

const SubmittedCard = ({ draft }: { draft: IntakeDraft }) => {
	const resolution = draft.resolution;
	if (!resolution) return null;
	return (
		<FernieCardShell accent="success" branded tinted>
			<FernieCardHeader accent="success" branded pillLabel={`Submitted · ${intakeKindLabel(draft.intakeKind)}`} />
			<Box sx={{ alignItems: "flex-start", display: "flex", gap: 1, px: 2, pt: 1.25 }}>
				<Icon sx={{ color: "success.main", fontSize: 16, position: "relative", top: "2px" }}>check_circle</Icon>
				<BodyTextSmall bold sx={{ color: "text.primary", lineHeight: 1.5 }}>
					Submitted your {intakeKindNoun(resolution.intakeKind)} — ref {resolution.referenceId}
				</BodyTextSmall>
			</Box>
			<Box sx={{ px: 2, pt: 1 }}>
				<BodyTextSmall bold sx={{ color: "text.primary", display: "block" }}>
					{resolution.title}
				</BodyTextSmall>
				<BodyTextSmall sx={{ color: "text.secondary", display: "block", lineHeight: 1.45, pt: 0.5 }}>
					{resolution.description}
				</BodyTextSmall>
				{resolution.intakeKind === "BUG" && resolution.severity && (
					<CaptionText sx={{ color: "text.disabled", display: "block", pt: 0.75 }}>
						Severity: {severityLabel(resolution.severity)}
					</CaptionText>
				)}
			</Box>
			<Box sx={{ px: 2, pb: 1.5, pt: 1 }} />
		</FernieCardShell>
	);
};

const DeclinedCard = ({ draft }: { draft: IntakeDraft }) => {
	const resolution = draft.resolution;
	if (!resolution) return null;
	const message =
		resolution.message ??
		`I couldn't submit your ${intakeKindNoun(resolution.intakeKind)} just now. Nothing was sent — please try again in a moment.`;
	return (
		<FernieCardShell accent="error" branded tinted>
			<FernieCardHeader accent="error" branded pillLabel={`Not submitted · ${intakeKindLabel(draft.intakeKind)}`} />
			<Box sx={{ alignItems: "flex-start", display: "flex", gap: 1, px: 2, py: 1.25 }}>
				<Icon sx={{ color: "error.main", fontSize: 16, position: "relative", top: "2px" }}>report</Icon>
				<BodyTextSmall sx={{ color: "error.main", fontWeight: 500, lineHeight: 1.5 }}>{message}</BodyTextSmall>
			</Box>
		</FernieCardShell>
	);
};

const FernieIntakeCard = (props: FernieIntakeCardProps) => {
	const { resolution } = props.draft;
	if (resolution?.state === "SUBMITTED") return <SubmittedCard draft={props.draft} />;
	if (resolution?.state === "DECLINED") return <DeclinedCard draft={props.draft} />;
	return <CaptureCard {...props} />;
};

export default FernieIntakeCard;
